/**
 * Fleet list (`/flotte` — fiches 22756 / 22987): the design's search card
 * above the vehicles table, hosted by the shared `DataTableCard` (detached
 * toolbar, server-filtered inline mode) from the declarative config of
 * `./fleetListConfig.ts`:
 *
 * - stable columns `immatriculation` (IMMAT_VEH), `marqueModele`
 *   (MARQ_MODL_VEH), `dateEntree` (DTENT_POL), `dateSortie` (DTSOR_POL,
 *   empty when null) and `statut` (VEH_SITUATION badge);
 * - **server-side** search « Rechercher par immat, marque, modèle… »: the
 *   debounced (300 ms) text goes up through `onFiltersChange` and the page
 *   re-fetches `WS2_EA_TAB_GEN_VEH` with it as `query` (blank ⇒ not sent);
 *   nothing is filtered client-side;
 * - row click → the vehicle detail, the row's immat / marque-modèle carried
 *   as router state for the detail header;
 * - below 768px `DataList` renders its own mobile card layout.
 *
 * Configurable: `DataTableCard` merges `_layoutOverrides` (columns rename /
 * hide / show / reorder / tooltips, default sort, search placeholder / hide,
 * title) and publishes the base config under `extranet.fleetListCard`. No
 * title at baseline — the page header carries « Flotte automobile » and the
 * counts subtitle, as in the design; a `setTitle` rule adds a title row.
 *
 * « Ajouter un véhicule » / « Sortir » are out of scope (no spec).
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Car } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import type { FleetListFilters, FleetVehicle } from '../../api/fleetService';
import { DataTableCard } from '../shared/DataTableCard';
import type { ListToolbarQuery } from '../shared/listToolbarConfig';
import { FLEET_COMPONENT_IDS } from './fleetComponentIds';
import { buildFleetListConfig, type FleetListRow } from './fleetListConfig';
import { vehiclePath, type VehicleRouteState } from './vehicleNavigation';

export type FleetListCardProps = {
  /** The fleet, in API order (already searched by VEOS). */
  vehicles: readonly FleetVehicle[];
  loading?: boolean;
  error?: string | null;
  /** Toolbar search as the `WS2_EA_TAB_GEN_VEH` filter (on mount and on change) — page-wired. */
  onFiltersChange?: (filters: FleetListFilters) => void;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
};

export function FleetListCard({
  vehicles,
  loading = false,
  error = null,
  onFiltersChange,
  _layoutOverrides,
}: Readonly<FleetListCardProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();

  // Per-row context: the click target of each row, rebuilt with the rows so
  // the singleton row-click handler always reads fresh closures.
  const rows = useMemo<FleetListRow[]>(
    () =>
      vehicles.map((vehicle) => {
        const { idNpol, idVeh } = vehicle;
        if (idNpol === null || idVeh === null) return { ...vehicle };
        const state: VehicleRouteState = {
          immat: vehicle.immatriculation,
          marqueModele: vehicle.marqueModele,
        };
        return {
          ...vehicle,
          __ctx: {
            onOpen: () => {
              void navigate(vehiclePath(tenantSlug, idNpol, idVeh), { state });
            },
          },
        };
      }),
    [vehicles, navigate, tenantSlug],
  );

  const baseConfig = useMemo(() => buildFleetListConfig(rows), [rows]);

  // Toolbar → VEOS filter: search → `query`.
  const handleToolbarChange = useCallback(
    (query: ListToolbarQuery) => {
      onFiltersChange?.({ query: query.search });
    },
    [onFiltersChange],
  );

  return (
    <DataTableCard
      componentId={FLEET_COMPONENT_IDS.fleetList}
      icon={Car}
      config={baseConfig}
      rowCount={vehicles.length}
      loading={loading}
      error={error}
      toolbar
      toolbarPlacement="detached"
      onToolbarChange={handleToolbarChange}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
